import {TouchEvent, forwardRef, useEffect, useState} from "react";
import {createPortal} from "react-dom";
import { useAppSelector } from "redux-share/hooks";
import { ConditionName, IPopoverProps, zIndex } from "interfaces";
import { conditions } from "helpers";
import { shallowEqual } from 'react-redux';
import dynamic from "next/dynamic";

const CloseIcon = dynamic(() => import("../../icon-components/closeIcon"));

const Popover = forwardRef(({ children, status, onClose, type, classNames, parentClassNames, styles, isBlurBackground, isFullHeight = false, isFullScreen, isHeader, headerTitle = "", customHeader, isHasCatchUpLineOnTop = false, animation, scrollbar = false, isFocusOnInput = false, showCloseIcon = true}: IPopoverProps, ref: any) => {

    const [showPopover, setShowPopover] = useState<boolean>(status!);

    const [maxHeight, setMaxHeight] = useState<number>(0);
    const [maxHeightOnTouchMove, setMaxHeightOnTouchMove] = useState<number>(0);
    const [zIndexOnCatchUp, setZIndexOnCatchUp] = useState<number>(0);

    const { videoElementSizeInfo } = useAppSelector(({ videoData: { videoElementSizeInfo, isComplexPortrait } }) => ({ videoElementSizeInfo, isComplexPortrait }), shallowEqual);

    useEffect(() => {
        let timeout: any = undefined

        if (status) {
            setShowPopover(true);
        } else {
            timeout = setTimeout(() => {
                onClose(false);
                setMaxHeightOnTouchMove(0);
                setShowPopover(false);
            }, 500);
        }

        return () => clearTimeout(timeout);
    }, [status])

    useEffect(() => {
        if (conditions(ConditionName.isComplexPortrait) && status) setMaxHeight(window.innerHeight- videoElementSizeInfo.bottom)
    }, [status])

    const handleTouchMove = (event: any) => {
        if(event.touches[0].clientY >= videoElementSizeInfo.top) setMaxHeightOnTouchMove(window.innerHeight - event.touches[0].clientY);
        else setMaxHeightOnTouchMove(window.innerHeight - videoElementSizeInfo.top);
        setZIndexOnCatchUp(zIndex.maxPopover);
    }

    const handleTouchEnd = () => {
        if (maxHeightOnTouchMove !== 0) {
            if (maxHeightOnTouchMove > maxHeight) {
                if (maxHeightOnTouchMove - maxHeight > ((window.innerHeight - videoElementSizeInfo.top - maxHeight) / 2)) {
                    setMaxHeightOnTouchMove(window.innerHeight - videoElementSizeInfo.top);
                    setZIndexOnCatchUp(zIndex.maxPopover)
                } else {
                    setMaxHeightOnTouchMove(0);
                    setTimeout(() => setZIndexOnCatchUp(0), 500);
                }
            } else {
                if (maxHeightOnTouchMove > maxHeight / 2) setMaxHeightOnTouchMove(maxHeight);
                else {
                    setMaxHeightOnTouchMove(0);
                    setMaxHeight(0)
                    onClose()
                }
                setZIndexOnCatchUp(0);
            }
        }
    }


    const content = (
        <>
            {isBlurBackground && <div
                className={`pl-w-screen pl-h-screen pl-fixed pl-top-0 pl-right-0 pl-bg-black/[0.7] animate__animated animate__faster ${status ? "animate__fadeIn" : "animate__fadeOut"}`}
                style={{zIndex: zIndex.popoverBlurBackground}}/>}
            <div
                className={`${type === "modal" && "pl-fixed pl-top-0 pl-left-0 pl-flex pl-w-full pl-h-full pl-justify-center pl-items-end sm:pl-items-center"} ${parentClassNames}`}
                style={{zIndex: isFullHeight && !isFocusOnInput ? zIndexOnCatchUp : zIndex.popoverContent}}>
                <div
                    className={
                        `pl-flex pl-flex-col pl-rounded-[8px] animate__animated animate__faster pl-bg-white/[0.9] dark:pl-bg-black/[0.7]
                        ${isFullHeight && "pl-h-full"}
                        ${animation ? animation : ((window.innerWidth > 768 && type === "modal") ? (status ? "animate__fadeIn" : "animate__fadeOut") : (status ? "animate__fadeInUp" : "animate__fadeOutDown"))}
                        ${conditions(ConditionName.isComplexPortrait) ? `!pl-fixed !pl-right-0 !pl-left-0 !pl-bottom-0 !pl-w-full !pl-mb-0 pl-rounded-[12px] !pl-rounded-b-none !pl-bg-white dark:!pl-bg-dark-3` : "before:pl-hidden"} 
                        ${conditions(ConditionName.isFullMobilePortrait) && `!pl-fixed !pl-mx-auto !pl-mb-[0px] !pl-right-0 !pl-left-0 ${type === "modal" ? "!pl-w-screen" : "!pl-w-[95vw] !pl-bottom-[85px]"}`}
                        ${type !== "modal" && "pl-absolute pl-bottom-full pl-overflow-auto no-scrollbar"}
                        ${classNames}
                        `
                    }
                    style={{
                        maxHeight: isFullScreen ? "unset" : maxHeightOnTouchMove ? maxHeightOnTouchMove : (maxHeight ? maxHeight : conditions(ConditionName.isComplexPortrait) ? 0 : ""),
                        zIndex: zIndex.popoverContent, 
                        ...styles
                    }}
                    ref={ref}
                >
                    <>
                        {isHeader && (
                            <div 
                                onTouchMove={isHasCatchUpLineOnTop ? handleTouchMove : () => {}} 
                                onTouchEndCapture={isHasCatchUpLineOnTop ? handleTouchEnd : () => {}}
                            >
                                {isHasCatchUpLineOnTop && <div
                                    className="pl-absolute pl-top-[5px] pl-right-[50%] pl-mr-[-25px] pl-text-center pl-w-[60px] pl-h-[3px] pl-rounded-[20px] pl-bg-secondary"/>}
                                {isHeader && (
                                    <div className="pl-mb-4"
                                        style={{ boxShadow: conditions(ConditionName.isDarkMode) ? "0px 8px 20px 0px rgba(178, 191, 205, 0.16)" : "0px 8px 20px 0px rgba(178, 191, 205, 0.16)"}}>
                                        {customHeader ? customHeader : (
                                            <div
                                                className="pl-flex pl-items-center pl-justify-between pl-py-[19px] pl-px-4">
                                                <p className="pl-text-[18px] pl-text-black dark:pl-text-white pl-font-bold">{headerTitle}</p>
                                                {showCloseIcon && (
                                                    <CloseIcon
                                                        className={`pl-cursor-pointer ${conditions(ConditionName.isDarkMode) ? "pl-stroke-dark-5" : "pl-stroke-secondary-400"}`}
                                                        onClick={onClose}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        <div className={`pl-overflow-y-auto pl-h-full pl-w-full ${!scrollbar && "no-scrollbar"}`}
                             onTouchMove={(event: TouchEvent<HTMLDivElement>) => event.stopPropagation()}>{children}</div>
                    </>
                </div>
            </div>
        </>
    )

    return <>{showPopover && <>{type === "modal" ? createPortal(<>{content}</>, document.getElementById("dialog-react-root-videoPlayer")!) :
        <div>{content}</div>}</>}</>

})

Popover.displayName = "popover";

export default Popover;